import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { CheckCircle, Clock, ChefHat, Package, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axios from 'axios';
import { API } from '@/config/api';
import { orderPath } from '../lib/orderPath';
import { OnlinePaymentStatus, forgetOrder, rememberedOrder } from '@/components/checkout';
import { useCartStore } from '@/store';

// An online order that is not paid yet: the hosted payment page sends the guest back
// here (/order/status/<id>?t=), so this page shows the payment until the gateway's verdict.
const UNPAID = ['pending', null, undefined, ''];

export default function OrderStatusPage() {
  const { orderNumber, orderId } = useParams();
  const { restaurantSlug, clearCart } = useCart();
  const clearSiteCart = useCartStore((st) => st.clearCart);
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('t') || '';
  const returnedCancelled = searchParams.get('cancelled') === '1';
  const navigate = useNavigate();
  
  // Use orderNumber or orderId (support both routes for backwards compatibility)
  const orderIdToFetch = orderNumber || orderId;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchOrder = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      const response = await axios.get(`${API}/orders/public/${orderIdToFetch}`, { params: { t: accessToken } });
      setOrder(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load order:', err);
      setError(
        err.response?.status === 404
          ? 'Order not found. Please check your order number.'
          : 'Unable to load order status. Please try again.'
      );
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [orderIdToFetch, accessToken]);

  // Initial fetch
  useEffect(() => {
    if (orderIdToFetch) {
      fetchOrder();
    }
  }, [orderIdToFetch, fetchOrder]);

  // Back from the payment page with the order already paid: this browser's cart is spent.
  useEffect(() => {
    if (!order || UNPAID.includes(order.payment_status) || !rememberedOrder(orderIdToFetch)) return;
    try { clearCart?.(); } catch { /* the order is paid either way */ }
    Promise.resolve(clearSiteCart?.()).catch(() => {});
    forgetOrder();
  }, [order, orderIdToFetch, clearCart, clearSiteCart]);

  // Auto-refresh polling (every 12 seconds)
  useEffect(() => {
    if (!order || error) return;

    const interval = setInterval(() => {
      fetchOrder(true); // Silent refresh
    }, 12000); // 12 seconds

    return () => clearInterval(interval);
  }, [order, error, fetchOrder]);

  const getStatusStep = (status) => {
    const statusMap = {
      'pending': 0,
      'received': 1,
      'preparing': 2,
      'ready': 3,
      'completed': 3,
      'delivered': 3
    };
    return statusMap[status?.toLowerCase()] || 1;
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000); // seconds
    
    if (diff < 60) return 'Just now';
    if (diff < 120) return '1 minute ago';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    return formatTime(lastUpdated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
          <div className="mt-6 space-y-3">
            <Button 
              onClick={() => fetchOrder()}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </Button>
            <Button 
              onClick={() => navigate(orderPath(restaurantSlug ? `storefront/${restaurantSlug}` : ''))}
              variant="outline"
              className="w-full"
            >
              Back to Menu
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  if (order.status === 'awaiting_payment' && UNPAID.includes(order.payment_status)) {
    const onPaid = async () => {
      if (rememberedOrder(orderIdToFetch)) {
        // this browser placed the order: its cart is spent
        try { clearCart?.(); } catch { /* the order is paid either way */ }
        try { await clearSiteCart?.(); } catch { /* the order is paid either way */ }
      }
      forgetOrder();
      // stay on the order status page: it now shows the paid order and its progress
      fetchOrder();
    };
    return (
      <OnlinePaymentStatus orderId={orderIdToFetch} accessToken={accessToken}
        cancelledReturn={returnedCancelled} onPaid={onPaid} />
    );
  }

  const currentStep = getStatusStep(order.status);
  const statusSteps = [
    {
      step: 1,
      label: 'Received',
      icon: CheckCircle,
      description: 'Order received'
    },
    {
      step: 2,
      label: 'Preparing',
      icon: ChefHat,
      description: 'Kitchen is preparing your order'
    },
    {
      step: 3,
      label: 'Ready',
      icon: Package,
      description: 'Ready for pickup'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-8" data-testid="order-status-page" data-payment-status={order.payment_status}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Order Status</h1>
              <p className="text-sm text-gray-600">Order #{orderIdToFetch}</p>
            </div>
            <Button
              onClick={() => fetchOrder(true)}
              variant="ghost"
              size="sm"
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Order Info Card */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Current Status
                </h2>
                <p className="text-3xl font-bold text-blue-600 capitalize mt-1" data-testid="order-status-current">
                  {order.status}
                </p>
              </div>
              {order.estimated_pickup_time && (
                <div className="text-right">
                  <p className="text-sm text-gray-600">Estimated Pickup</p>
                  <p className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-gray-400" />
                    {order.estimated_pickup_time}
                  </p>
                </div>
              )}
            </div>
            
            {lastUpdated && (
              <p className="text-xs text-gray-500">
                Last updated: {formatLastUpdated()}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Status Timeline */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Order Progress
            </h2>
            
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute top-8 left-8 right-8 h-0.5 bg-gray-200">
                <div 
                  className="h-full bg-blue-600 transition-all duration-500"
                  style={{ 
                    width: currentStep === 1 ? '0%' : currentStep === 2 ? '50%' : '100%' 
                  }}
                />
              </div>

              {/* Status Steps */}
              <div className="relative space-y-6">
                {statusSteps.map((step) => {
                  const isComplete = step.step < currentStep;
                  const isCurrent = step.step === currentStep;
                  const isPending = step.step > currentStep;

                  const Icon = step.icon;

                  return (
                    <div 
                      key={step.step}
                      className="flex items-start gap-4"
                    >
                      {/* Icon Circle */}
                      <div 
                        className={`
                          relative flex items-center justify-center w-16 h-16 rounded-full border-4 flex-shrink-0
                          ${isComplete || isCurrent 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'bg-white border-gray-200'
                          }
                          ${isCurrent ? 'ring-4 ring-blue-100' : ''}
                          transition-all duration-500
                        `}
                      >
                        {isComplete ? (
                          <CheckCircle className="h-8 w-8 text-white" />
                        ) : (
                          <Icon 
                            className={`h-8 w-8 ${
                              isCurrent ? 'text-white' : 'text-gray-400'
                            }`} 
                          />
                        )}
                      </div>

                      {/* Text */}
                      <div className="flex-1 pt-3">
                        <h3 
                          className={`text-lg font-semibold ${
                            isComplete || isCurrent 
                              ? 'text-gray-900' 
                              : 'text-gray-400'
                          }`}
                        >
                          {step.label}
                        </h3>
                        <p 
                          className={`text-sm ${
                            isComplete || isCurrent 
                              ? 'text-gray-600' 
                              : 'text-gray-400'
                          }`}
                        >
                          {step.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order Details Summary */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Order Details
            </h2>
            
            <div className="space-y-3">
              {order.items && order.items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {item.quantity}x {item.name}
                  </span>
                  <span className="font-medium text-gray-900">
                    ${item.total_price?.toFixed(2)}
                  </span>
                </div>
              ))}
              
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-medium">${order.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600 mt-1">
                  <span>Tax</span>
                  <span className="font-medium">${order.tax?.toFixed(2)}</span>
                </div>
                {order.tip > 0 && (
                  <div className="flex justify-between text-gray-600 mt-1">
                    <span>Tip</span>
                    <span className="font-medium">${order.tip?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-gray-900 mt-2 pt-2 border-t">
                  <span>Total</span>
                  <span>${(order.customer_paid_total || order.total)?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate(orderPath(`confirmation/${orderIdToFetch}?t=${accessToken}`))}
            variant="outline"
            className="w-full h-12 text-lg"
          >
            View Order Confirmation
          </Button>
          <Button
            onClick={() => navigate(orderPath(restaurantSlug ? `storefront/${restaurantSlug}` : ''))}
            variant="outline"
            className="w-full h-12 text-lg"
          >
            Back to Menu
          </Button>
        </div>

        {/* Help Text */}
        <div className="text-center text-sm text-gray-500">
          <p>This page refreshes automatically every 12 seconds</p>
          {order.customer_phone && (
            <p className="mt-1">
              We'll notify you at {order.customer_phone} when your order is ready
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
